import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminUsersListAction, dashboardStatsAction } from "../Action/action";

function changeClass(value) {
  const n = Number(value);
  if (Number.isNaN(n) || n === 0) return "kpi-change";
  return n > 0 ? "kpi-change up" : "kpi-change down";
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active" || s === "published") return "badge badge-success";
  if (s === "coming soon" || s === "draft" || s === "inactive") return "badge badge-warning";
  if (s === "archived") return "badge badge-muted";
  return "badge";
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enrollRange, setEnrollRange] = useState(30);
  const [stats, setStats] = useState({
    userCount: null,
    thisMonthUsers: null,
    lastMonthUsers: null,
    userGrowthPercent: null,

    activeCoursesCount: null,
    newCoursesThisMonth: null,

    journalEntriesCount: null,
    thisMonthJournalEntries: null,
    lastMonthJournalEntries: null,
    journalGrowthPercent: null,

    coachingHacksCount: null,
    thisWeekNewHacks: null,
    lastWeekNewHacks: null,
    hacksWeekDelta: null,

    topCourses: [],
    recentHacks: [],
    enrollmentTrend: []
  });
  const [recentUsers, setRecentUsers] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [statsRes, usersRes] = await Promise.all([
          dashboardStatsAction(),
          adminUsersListAction()
        ]);

        if (cancelled) return;

        if (statsRes?.success) {
          const d = statsRes.data || {};
          setStats({
            userCount: d.userCount ?? null,
            thisMonthUsers: d.thisMonthUsers ?? null,
            lastMonthUsers: d.lastMonthUsers ?? null,
            userGrowthPercent: d.userGrowthPercent ?? null,

            activeCoursesCount: d.activeCoursesCount ?? null,
            newCoursesThisMonth: d.newCoursesThisMonth ?? null,

            journalEntriesCount: d.journalEntriesCount ?? null,
            thisMonthJournalEntries: d.thisMonthJournalEntries ?? null,
            lastMonthJournalEntries: d.lastMonthJournalEntries ?? null,
            journalGrowthPercent: d.journalGrowthPercent ?? null,

            coachingHacksCount: d.coachingHacksCount ?? null,
            thisWeekNewHacks: d.thisWeekNewHacks ?? null,
            lastWeekNewHacks: d.lastWeekNewHacks ?? null,
            hacksWeekDelta: d.hacksWeekDelta ?? null,

            topCourses: Array.isArray(d.topCourses) ? d.topCourses : [],
            recentHacks: Array.isArray(d.recentHacks) ? d.recentHacks : [],
            enrollmentTrend: Array.isArray(d.enrollmentTrend) ? d.enrollmentTrend : []
          });
        } else {
          setStats((prev) => ({
            ...prev,
            userCount: null,
            activeCoursesCount: null,
            journalEntriesCount: null,
            coachingHacksCount: null,
            topCourses: [],
            recentHacks: [],
            enrollmentTrend: []
          }));
        }

        if (usersRes?.success && Array.isArray(usersRes.data)) {
          setRecentUsers(usersRes.data.slice(0, 5));
        } else {
          setRecentUsers([]);
        }

        const anyErrorMsg =
          (!statsRes?.success ? (statsRes?.message || statsRes?.msg) : "") ||
          (!usersRes?.success ? (usersRes?.message || usersRes?.msg) : "");
        if (anyErrorMsg) setError(anyErrorMsg);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load dashboard.");
        setStats((prev) => ({
          ...prev,
          userCount: null,
          topCourses: [],
          recentHacks: [],
          enrollmentTrend: []
        }));
        setRecentUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const recentRows = useMemo(() => {
    return recentUsers.map((u) => {
      const name = u?.name || u?.username || "—";
      const email = u?.email || "—";
      const createdAt = u?.created_at || u?.createdAt || u?.created || null;
      let date = "—";
      if (createdAt) {
        const d = new Date(createdAt);
        date = Number.isNaN(d.getTime()) ? String(createdAt) : d.toLocaleDateString();
      }
      return { id: u?.id ?? `${email}-${name}`, name, email, date };
    });
  }, [recentUsers]);

  const userGrowthLabel = useMemo(() => {
    if (loading) return "Loading…";
    const pct = stats.userGrowthPercent;
    if (pct !== null && pct !== undefined && pct !== "") {
      const n = Number(pct);
      if (!Number.isNaN(n)) {
        const arrow = n >= 0 ? "↑" : "↓";
        return `${arrow} ${Math.abs(n)}% this month`;
      }
    }
    const tm = stats.thisMonthUsers;
    if (tm !== null && tm !== undefined && tm !== "") {
      const n = Number(tm);
      if (!Number.isNaN(n)) return `↑ ${n} new this month`;
    }
    return "—";
  }, [loading, stats.thisMonthUsers, stats.userGrowthPercent]);

  const coursesGrowthLabel = useMemo(() => {
    if (loading) return "Loading…";
    const n = stats.newCoursesThisMonth;
    if (n !== null && n !== undefined && n !== "") {
      const v = Number(n);
      if (!Number.isNaN(v)) return `↑ ${v} new this month`;
    }
    return "—";
  }, [loading, stats.newCoursesThisMonth]);

  const journalGrowthLabel = useMemo(() => {
    if (loading) return "Loading…";
    const pct = stats.journalGrowthPercent;
    if (pct !== null && pct !== undefined && pct !== "") {
      const n = Number(pct);
      if (!Number.isNaN(n)) {
        const arrow = n >= 0 ? "↑" : "↓";
        return `${arrow} ${Math.abs(n)}% this month`;
      }
    }
    const tm = stats.thisMonthJournalEntries;
    if (tm !== null && tm !== undefined && tm !== "") {
      const n = Number(tm);
      if (!Number.isNaN(n)) return `↑ ${n} this month`;
    }
    return "—";
  }, [loading, stats.journalGrowthPercent, stats.thisMonthJournalEntries]);

  const hacksGrowthLabel = useMemo(() => {
    if (loading) return "Loading…";
    const thisWeek = stats.thisWeekNewHacks;
    const delta = stats.hacksWeekDelta;
    const tw = Number(thisWeek);
    const d = Number(delta);
    if (!Number.isNaN(tw) && !Number.isNaN(d)) {
      const arrow = d >= 0 ? "↑" : "↓";
      return `${arrow} ${tw} new this week`;
    }
    if (!Number.isNaN(tw)) return `↑ ${tw} new this week`;
    return "—";
  }, [loading, stats.hacksWeekDelta, stats.thisWeekNewHacks]);

  const enrollmentSeries = useMemo(() => {
    const all = Array.isArray(stats.enrollmentTrend) ? stats.enrollmentTrend : [];
    const n = Number(enrollRange) || 30;
    return all.slice(-n);
  }, [enrollRange, stats.enrollmentTrend]);

  const enrollmentMax = useMemo(() => {
    let max = 0;
    for (const p of enrollmentSeries) {
      const c = Number(p?.count || 0);
      if (c > max) max = c;
    }
    return max || 1;
  }, [enrollmentSeries]);

  const enrollmentTotal = useMemo(() => {
    return enrollmentSeries.reduce((sum, p) => sum + Number(p?.count || 0), 0);
  }, [enrollmentSeries]);

  const formatChartDay = (day) => {
    if (!day) return "";
    const d = new Date(`${String(day).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(d.getTime())) return String(day).slice(5);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Dashboard data unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon blue">👥</div>
          <div className="kpi-info">
            <h3>{loading ? "…" : (stats.userCount ?? "—")}</h3>
            <p>Total Users</p>
            <div className={changeClass(stats.userGrowthPercent ?? stats.thisMonthUsers)}>
              {userGrowthLabel}
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon orange">🎓</div>
          <div className="kpi-info">
            <h3>{loading ? "…" : (stats.activeCoursesCount ?? "—")}</h3>
            <p>Active Courses</p>
            <div className={changeClass(stats.newCoursesThisMonth)}>{coursesGrowthLabel}</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon green">📓</div>
          <div className="kpi-info">
            <h3>{loading ? "…" : (stats.journalEntriesCount ?? "—")}</h3>
            <p>Journal Entries</p>
            <div className={changeClass(stats.journalGrowthPercent ?? stats.thisMonthJournalEntries)}>
              {journalGrowthLabel}
            </div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon red">💡</div>
          <div className="kpi-info">
            <h3>{loading ? "…" : (stats.coachingHacksCount ?? "—")}</h3>
            <p>Coaching Hacks</p>
            <div className={changeClass(stats.hacksWeekDelta ?? stats.thisWeekNewHacks)}>
              {hacksGrowthLabel}
            </div>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <Link to="/courses/new" className="btn btn-primary">
          ➕ Add New Course
        </Link>
        <Link to="/coaching-hacks/new" className="btn btn-accent">
          💡 Add Coaching Hack
        </Link>
        <Link to="/users" className="btn btn-outline">
          👥 View All Users
        </Link>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <h2>Course Enrollments</h2>
            <select
              className="form-control w-130"
              value={String(enrollRange)}
              onChange={(e) => setEnrollRange(Number(e.target.value) || 30)}
            >
              <option value="30">Last 30 days</option>
              <option value="7">Last 7 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
          {loading ? (
            <div className="chart-placeholder">Loading…</div>
          ) : enrollmentSeries.length === 0 ? (
            <div className="chart-placeholder">No enrollment data yet.</div>
          ) : (
            <div className="dash-chart">
              <div className="dash-chart-meta">
                <span>{enrollmentTotal} enrollments</span>
                <span>Peak {enrollmentMax === 1 && enrollmentTotal === 0 ? 0 : enrollmentMax}/day</span>
              </div>
              <div className="dash-chart-bars" role="img" aria-label="Enrollment trend">
                {enrollmentSeries.map((p) => {
                  const count = Number(p.count || 0);
                  const heightPx =
                    count <= 0 ? 2 : Math.max(6, Math.round((count / enrollmentMax) * 170));
                  const dayLabel = formatChartDay(p.day);
                  return (
                    <div key={p.day} className="dash-chart-col">
                      <div className="dash-chart-bar-track">
                        <div
                          className="dash-chart-bar"
                          style={{ height: `${heightPx}px` }}
                          aria-label={`${dayLabel}: ${count} enrollments`}
                        >
                          <span className="dash-chart-tip">
                            <strong>{count}</strong>
                            {count === 1 ? " enrollment" : " enrollments"}
                            <br />
                            {dayLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Recent Sign-ups</h2>
            <Link to="/users" className="action-link">
              View all →
            </Link>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3}>
                      <div className="empty-state" style={{ padding: "28px 12px" }}>
                        Loading…
                      </div>
                    </td>
                  </tr>
                ) : recentRows.length ? (
                  recentRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td>{r.email}</td>
                      <td>{r.date}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>
                      <div className="empty-state" style={{ padding: "28px 12px" }}>
                        No recent sign-ups found.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid-2 mt-6">
        <div className="card">
          <div className="card-header">
            <h2>Top Courses by Enrollment</h2>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Age Group</th>
                  <th>Enrolled</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4}>
                      <div className="empty-state" style={{ padding: "28px 12px" }}>
                        Loading…
                      </div>
                    </td>
                  </tr>
                ) : stats.topCourses.length ? (
                  stats.topCourses.map((c) => (
                    <tr key={c.id}>
                      <td>{c.title}</td>
                      <td>{c.age_group || "—"}</td>
                      <td>
                        <strong>{c.enrolled}</strong>
                      </td>
                      <td>
                        <span className={statusBadgeClass(c.status)}>{c.status}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <div className="empty-state" style={{ padding: "28px 12px" }}>
                        No courses found.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Recent Coaching Hacks</h2>
            <Link to="/coaching-hacks" className="action-link">
              View all →
            </Link>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Favorites</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3}>
                      <div className="empty-state" style={{ padding: "28px 12px" }}>
                        Loading…
                      </div>
                    </td>
                  </tr>
                ) : stats.recentHacks.length ? (
                  stats.recentHacks.map((h) => (
                    <tr key={h.id}>
                      <td>{h.title}</td>
                      <td>{h.favorites}</td>
                      <td>
                        <span className={statusBadgeClass(h.status)}>{h.status}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>
                      <div className="empty-state" style={{ padding: "28px 12px" }}>
                        No coaching hacks found.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
